import React, { useState, useEffect } from 'react';
import { useParentPortal } from '../contexts/ParentPortalContext';
import { useFamilyContext } from '../../../../contexts/FamilyContext';
import { useCollegeContext } from '../../../../contexts/CollegeContext';
import { collegeJourneyService } from '../../../../lib/college-journey-service';
import { supabase } from '../../../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Plus, Search, Filter, Star, MapPin, DollarSign, Users, TrendingUp,
  Edit3, Trash2, X, ChevronDown, ChevronUp, Database, Sparkles, BookOpen,
  EyeOff, Clock, AlertCircle, CheckCircle, Target, School, Shield, Info
} from 'lucide-react';

const CollegeManagementSection = () => {
  const { selectedStudent, studentProgress } = useParentPortal();
  const { currentUser, family } = useFamilyContext();
  const { updateCollege } = useCollegeContext();
  const navigate = useNavigate();

  const [parentColleges, setParentColleges] = useState([]);
  const [studentColleges, setStudentColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingCollege, setAddingCollege] = useState(false);
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [newCollege, setNewCollege] = useState({
    name: '',
    location: '',
    category: 'target',
    notes: '',
    early_action: false,
    early_decision: false
  });

  // Load colleges when student changes
  useEffect(() => {
    if (selectedStudent) {
      loadColleges();
    }
  }, [selectedStudent]);

  const loadColleges = async () => {
    if (!selectedStudent) return;

    setLoading(true);
    try {
      const studentId = selectedStudent.auth_user_id || selectedStudent.id;

      // Load parent-suggested colleges
      const { data: parentData, error: parentError } = await supabase
        .from('colleges')
        .select('*')
        .eq('family_id', family?.id)
        .eq('suggested_by_parent', true)
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });

      if (parentError) {
        console.error('Error loading parent colleges:', parentError);
      } else {
        setParentColleges(parentData || []);
      }

      // Load student's own colleges
      const { data: studentData, error: studentError } = await supabase
        .from('colleges')
        .select('*')
        .eq('user_id', studentId)
        .eq('suggested_by_parent', false)
        .order('created_at', { ascending: false });

      if (studentError) {
        console.error('Error loading student colleges:', studentError);
      } else {
        setStudentColleges(studentData || []);
      }
    } catch (error) {
      console.error('Error loading colleges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollege = async () => {
    if (!newCollege.name.trim() || !selectedStudent) return;

    try {
      const studentId = selectedStudent.auth_user_id || selectedStudent.id;

      const { error } = await supabase
        .from('colleges')
        .insert({
          name: newCollege.name,
          location: newCollege.location,
          category: newCollege.category,
          notes: newCollege.notes,
          early_action: newCollege.early_action,
          early_decision: newCollege.early_decision,
          suggested_by_parent: true,
          user_id: studentId,
          student_id: studentId,
          family_id: family?.id,
          created_by: currentUser.id
        });

      if (error) throw error;

      setNewCollege({
        name: '',
        location: '',
        category: 'target',
        notes: '',
        early_action: false,
        early_decision: false
      });
      setAddingCollege(false);
      loadColleges();
    } catch (error) {
      console.error('Error adding college:', error);
      alert('Failed to add college. Please try again.');
    }
  };

  const handleDeleteCollege = async (collegeId) => {
    if (!confirm('Are you sure you want to remove this college suggestion?')) return;

    try {
      const { error } = await supabase
        .from('colleges')
        .delete()
        .eq('id', collegeId)
        .eq('suggested_by_parent', true);

      if (error) throw error;
      loadColleges();
    } catch (error) {
      console.error('Error deleting college:', error);
      alert('Failed to remove college. Please try again.');
    }
  };

  const handleCategoryChange = async (collegeId, newCategory) => {
    try {
      const { error } = await supabase
        .from('colleges')
        .update({ category: newCategory })
        .eq('id', collegeId);

      if (error) throw error;
      loadColleges();
    } catch (error) {
      console.error('Error updating category:', error);
    }
  };

  // Filter colleges based on search and category
  const filteredParentColleges = parentColleges.filter(college => {
    const matchesSearch = college.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          college.location?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || college.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredStudentColleges = studentColleges.filter(college => {
    const matchesSearch = college.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          college.location?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || college.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Calculate statistics
  const stats = {
    total: parentColleges.length + studentColleges.length,
    reach: [...parentColleges, ...studentColleges].filter(c => c.category === 'reach' || c.category === 'reach').length,
    target: [...parentColleges, ...studentColleges].filter(c => c.category === 'target').length,
    safety: [...parentColleges, ...studentColleges].filter(c => c.category === 'safety').length
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'reach':
        return 'bg-coral-500 text-white';
      case 'target':
        return 'bg-azure-500 text-white';
      case 'safety':
        return 'bg-emerald-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };

  if (!selectedStudent) {
    return (
      <div className="text-center py-12">
        <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          No Student Selected
        </h2>
        <p className="text-slate-600">
          Select a student to manage their college strategy and suggestions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">College Strategy</h1>
        <p className="text-slate-600 mt-1">
          Help {selectedStudent.name} build a strategic college list
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-600">Total Colleges</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-coral-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-coral-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.reach}</div>
              <div className="text-sm text-slate-600">Reach Schools</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-azure-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-azure-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.target}</div>
              <div className="text-sm text-slate-600">Target Schools</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900">{stats.safety}</div>
              <div className="text-sm text-slate-600">Safety Schools</div>
            </div>
          </div>
        </div>
      </div>

      {/* Strategic Guidance Panel */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl border border-indigo-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Star className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-indigo-900 mb-2">
              Building a Balanced College List
            </h3>
            <p className="text-indigo-800 mb-3">
              A strategic college list typically includes 2-3 reach schools, 3-4 target schools, and 2-3 safety schools.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white/50 rounded-lg p-3">
                <h4 className="font-medium text-coral-900">Reach Schools</h4>
                <p className="text-coral-700 mt-1">Admission chance: 10-30%</p>
              </div>
              <div className="bg-white/50 rounded-lg p-3">
                <h4 className="font-medium text-azure-900">Target Schools</h4>
                <p className="text-azure-700 mt-1">Admission chance: 30-60%</p>
              </div>
              <div className="bg-white/50 rounded-lg p-3">
                <h4 className="font-medium text-emerald-900">Safety Schools</h4>
                <p className="text-emerald-700 mt-1">Admission chance: 60%+</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search colleges..."
              className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
            >
              <option value="all">All Categories</option>
              <option value="reach">Reach</option>
              <option value="target">Target</option>
              <option value="safety">Safety</option>
            </select>
            <button
              onClick={() => setAddingCollege(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
            >
              <Plus size={16} />
              Add College
            </button>
            <button
              onClick={() => navigate('/college/colleges')}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2"
            >
              <Database size={16} />
              Browse Database
            </button>
          </div>
        </div>
      </div>

      {/* Add College Form */}
      {addingCollege && (
        <div className="bg-white rounded-xl border-2 border-teal-500 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">Add College Suggestion</h3>
            <button
              onClick={() => setAddingCollege(false)}
              className="text-slate-500 hover:text-slate-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                College Name *
              </label>
              <input
                type="text"
                value={newCollege.name}
                onChange={(e) => setNewCollege({...newCollege, name: e.target.value})}
                placeholder="e.g., Stanford University"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={newCollege.location}
                onChange={(e) => setNewCollege({...newCollege, location: e.target.value})}
                placeholder="e.g., Stanford, CA"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Category
              </label>
              <select
                value={newCollege.category}
                onChange={(e) => setNewCollege({...newCollege, category: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
              >
                <option value="reach">Reach</option>
                <option value="target">Target</option>
                <option value="safety">Safety</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newCollege.early_action}
                  onChange={(e) => setNewCollege({...newCollege, early_action: e.target.checked})}
                  className="text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-slate-700">Early Action</span>
              </label>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newCollege.early_decision}
                  onChange={(e) => setNewCollege({...newCollege, early_decision: e.target.checked})}
                  className="text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-slate-700">Early Decision</span>
              </label>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Notes for {selectedStudent.name}
            </label>
            <textarea
              value={newCollege.notes}
              onChange={(e) => setNewCollege({...newCollege, notes: e.target.value})}
              placeholder="Why this college would be a good fit..."
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleAddCollege}
              disabled={!newCollege.name.trim()}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
            >
              Add College
            </button>
            <button
              onClick={() => setAddingCollege(false)}
              className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Parent's College Suggestions */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Your College Suggestions</h3>
            <span className="text-sm text-slate-600">
              {filteredParentColleges.length} {filteredParentColleges.length === 1 ? 'college' : 'colleges'}
            </span>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <Clock className="w-8 h-8 text-slate-400 mx-auto mb-2 animate-spin" />
              <p className="text-slate-600">Loading colleges...</p>
            </div>
          ) : filteredParentColleges.length === 0 ? (
            <div className="text-center py-8">
              <GraduationCap className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-slate-900 mb-2">
                {searchQuery || filterCategory !== 'all'
                  ? 'No matching colleges found'
                  : 'No colleges added yet'}
              </h4>
              <p className="text-slate-600">
                {searchQuery || filterCategory !== 'all'
                  ? 'Try adjusting your search or filters'
                  : `Start building a college list for ${selectedStudent.name}`}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredParentColleges.map((college) => (
                <div
                  key={college.id}
                  className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900 text-lg">{college.name}</h4>
                          {college.location && (
                            <div className="flex items-center gap-1 text-slate-600 text-sm mt-1">
                              <MapPin className="w-4 h-4" />
                              {college.location}
                            </div>
                          )}

                          <div className="flex gap-2 mt-3 flex-wrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(college.category)}`}>
                              {college.category?.toUpperCase()}
                            </span>
                            {college.early_action && (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                EARLY ACTION
                              </span>
                            )}
                            {college.early_decision && (
                              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 border border-violet-200">
                                EARLY DECISION
                              </span>
                            )}
                          </div>

                          {college.notes && (
                            <p className="text-sm text-slate-600 mt-3">{college.notes}</p>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <select
                            value={college.category}
                            onChange={(e) => handleCategoryChange(college.id, e.target.value)}
                            className="px-3 py-1 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-100 focus:border-teal-500"
                          >
                            <option value="reach">Reach</option>
                            <option value="target">Target</option>
                            <option value="safety">Safety</option>
                          </select>
                          <button
                            onClick={() => navigate(`/college/${college.id}`)}
                            className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                            title="View details"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCollege(college.id)}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove college"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Student's Colleges (Read-Only) */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-900">Student's Own Colleges</h3>
            <div className="flex items-center gap-2">
              <EyeOff className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">Read-only view</span>
            </div>
          </div>
        </div>

        <div className="p-6">
          {filteredStudentColleges.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-slate-900 mb-2">
                {searchQuery || filterCategory !== 'all'
                  ? 'No matching colleges found'
                  : 'No student colleges yet'}
              </h4>
              <p className="text-slate-600">
                {searchQuery || filterCategory !== 'all'
                  ? 'Try adjusting your search or filters'
                  : `${selectedStudent.name} hasn't added any colleges on their own`}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredStudentColleges.map((college) => (
                <div
                  key={college.id}
                  className="border border-slate-200 rounded-lg p-4 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`/college/${college.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900">{college.name}</h4>
                      {college.location && (
                        <p className="text-sm text-slate-600">{college.location}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          college.category === 'reach' || college.category === 'reach'
                            ? 'bg-coral-100 text-coral-700'
                            : college.category === 'target'
                            ? 'bg-azure-100 text-azure-700'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {college.category}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-stone-100 text-slate-700">
                          Student Added
                        </span>
                      </div>
                    </div>
                    <Edit3 className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollegeManagementSection;